import { formatDistanceToNow } from 'date-fns'
import type { VendorAdvisory } from '@/lib/types'
import { VendorAdvisoryList } from './VendorAdvisoryList'

/**
 * CISO-facing Remediation card — surfaces the classifier's patch signal on
 * the vulnerability detail page. States:
 *  A. `patchAvailable=true` + advisory URL — green dot, "Patch available",
 *     optional fix-version line, "Open vendor advisory" link.
 *  B. `patchAvailable=false` — muted dot, "Patch status unknown".
 *  C. `patchClassifiedAt=null` — classifier hasn't run; render nothing.
 *
 * V5 enhancement: when `vendorAdvisories` is provided and contains at least
 * one non-suppressed row, the card renders the multi-vendor
 * `<VendorAdvisoryList>` underneath the V3 patch-status badge, and demotes the
 * V3 "Open vendor advisory" single link to an "Also see: NVD reference" line
 * so the vendor-sourced advisories take the primary slot. When the list is
 * empty or undefined (flag off), the V3 layout is preserved unchanged.
 *
 * Field shapes mirror the public projection
 * (`apps/api/src/routes/vulnerabilities.routes.ts`):
 *   fix_version_hint → string | null
 *   primary_advisory_url → string | null
 *   patch_classified_at → ISO timestamp | null
 *   vendor_advisories → VendorAdvisory[] (V5 — non-suppressed only)
 * All four are populated by the V3 classifier + V5 vendor adapters.
 */

export interface RemediationSectionProps {
  patchAvailable: boolean | null
  fixVersionHint: string | null
  primaryAdvisoryUrl: string | null
  patchClassifiedAt: string | null
  /** V5 — vendor-sourced advisories. Pass undefined when the flag is off. */
  vendorAdvisories?: VendorAdvisory[]
}

function classifiedLabel(patchClassifiedAt: string): string {
  const parsed = new Date(patchClassifiedAt)
  if (Number.isNaN(parsed.getTime())) {
    return 'Classified from NVD'
  }
  return `Classified from NVD · ${formatDistanceToNow(parsed, { addSuffix: true })}`
}

export function RemediationSection({
  patchAvailable,
  fixVersionHint,
  primaryAdvisoryUrl,
  patchClassifiedAt,
  vendorAdvisories,
}: RemediationSectionProps) {
  if (!patchClassifiedAt) return null

  const label = classifiedLabel(patchClassifiedAt)
  const visibleV5 = (vendorAdvisories ?? []).filter(
    (a) => a.suppressedAt == null,
  )
  const hasV5 = visibleV5.length > 0

  if (patchAvailable) {
    return (
      <div
        role="region"
        aria-label="Remediation"
        className="rounded-lg border border-border bg-card p-4"
        data-testid="remediation-section"
      >
        <div className="flex items-center gap-2 text-sm">
          <span
            aria-hidden="true"
            className="h-2 w-2 rounded-full bg-emerald-500"
          />
          <span className="font-medium text-foreground">Patch available</span>
        </div>
        {fixVersionHint && (
          <p className="mt-1 text-sm text-foreground">
            <span className="text-muted-foreground">Fix:</span> {fixVersionHint}
          </p>
        )}
        {hasV5 && <VendorAdvisoryList advisories={visibleV5} />}
        {primaryAdvisoryUrl &&
          (hasV5 ? (
            // V5 present — demote the NVD/V3 single link to a muted "Also see"
            // so the vendor-sourced list takes primary visual weight.
            <p className="mt-3 text-xs text-muted-foreground">
              Also see:{' '}
              <a
                href={primaryAdvisoryUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                NVD reference <span aria-hidden="true">↗</span>
              </a>
            </p>
          ) : (
            <a
              href={primaryAdvisoryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              Open vendor advisory <span aria-hidden="true">↗</span>
            </a>
          ))}
        <p className="mt-2 text-xs text-muted-foreground">{label}</p>
      </div>
    )
  }

  return (
    <div
      role="region"
      aria-label="Remediation"
      className="rounded-lg border border-border bg-card p-4"
      data-testid="remediation-section"
    >
      <div className="flex items-center gap-2 text-sm">
        <span
          aria-hidden="true"
          className="h-2 w-2 rounded-full bg-muted-foreground/40"
        />
        <span className="text-foreground">Patch status unknown</span>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        No vendor advisory detected yet.
      </p>
      {hasV5 && <VendorAdvisoryList advisories={visibleV5} />}
      <p className="mt-2 text-xs text-muted-foreground">{label}</p>
    </div>
  )
}
